import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import apiClient from '../../../services/apiClient';
import { API_ENDPOINTS } from '../../../constants/apiEndpoints';

export const getGivingCampaigns = createAsyncThunk(
    'givingCampaigns/getGivingCampaigns',
    async (params = {}, { rejectWithValue }) => {
        try {
            const token = localStorage.getItem('access_token');
            if (!token) throw new Error('No token available');

            const response = await apiClient.get(API_ENDPOINTS.GIVING_CAMPAIGNS, { params });
            const { status, message, data } = response.data;

            if (status === 200) {
                return {
                    success: true,
                    message: message || 'Campaigns retrieved successfully',
                    data: data || {}
                };
            }
            return rejectWithValue({ message: message || 'Failed to fetch campaigns', status });
        } catch (error) {
            return rejectWithValue({ message: error.response?.data?.message || 'Failed to fetch campaigns' });
        }
    }
);

export const getGivingCampaign = createAsyncThunk(
    'givingCampaigns/getGivingCampaign',
    async (id, { rejectWithValue }) => {
        try {
            const token = localStorage.getItem('access_token');
            if (!token) throw new Error('No token available');

            const response = await apiClient.get(API_ENDPOINTS.GIVING_CAMPAIGN(id));
            const { status, message, data } = response.data;

            if (status === 200) {
                return {
                    success: true,
                    message: message || 'Campaign retrieved successfully',
                    campaign: data || null
                };
            }
            return rejectWithValue({ message: message || 'Failed to fetch campaign', status });
        } catch (error) {
            return rejectWithValue({ message: error.response?.data?.message || 'Failed to fetch campaign' });
        }
    }
);

export const createGivingCampaign = createAsyncThunk(
    'givingCampaigns/createGivingCampaign',
    async (campaignData, { rejectWithValue }) => {
        try {
            const token = localStorage.getItem('access_token');
            if (!token) throw new Error('No token available');

            const response = await apiClient.post(API_ENDPOINTS.GIVING_CAMPAIGN_CREATE, campaignData);
            const { status, message, data } = response.data;

            if (status === 201 || status === 200) {
                return {
                    success: true,
                    message: message || 'Campaign created successfully',
                    campaign: data || null
                };
            }
            return rejectWithValue({ message: message || 'Failed to create campaign', status });
        } catch (error) {
            return rejectWithValue({ message: error.response?.data?.message || 'Failed to create campaign' });
        }
    }
);

export const updateGivingCampaign = createAsyncThunk(
    'givingCampaigns/updateGivingCampaign',
    async ({ id, data }, { rejectWithValue }) => {
        try {
            const token = localStorage.getItem('access_token');
            if (!token) throw new Error('No token available');

            const response = await apiClient.put(API_ENDPOINTS.GIVING_CAMPAIGN_UPDATE(id), data);
            const { status, message, data: responseData } = response.data;

            if (status === 200) {
                return {
                    success: true,
                    message: message || 'Campaign updated successfully',
                    campaign: responseData || null
                };
            }
            return rejectWithValue({ message: message || 'Failed to update campaign', status });
        } catch (error) {
            return rejectWithValue({ message: error.response?.data?.message || 'Failed to update campaign' });
        }
    }
);

export const deleteGivingCampaign = createAsyncThunk(
    'givingCampaigns/deleteGivingCampaign',
    async (id, { rejectWithValue }) => {
        try {
            const token = localStorage.getItem('access_token');
            if (!token) throw new Error('No token available');

            const response = await apiClient.delete(API_ENDPOINTS.GIVING_CAMPAIGN_DELETE(id));
            const { status, message } = response.data;

            if (status === 200) {
                return {
                    success: true,
                    message: message || 'Campaign deleted successfully',
                    id: id
                };
            }
            return rejectWithValue({ message: message || 'Failed to delete campaign', status });
        } catch (error) {
            return rejectWithValue({ message: error.response?.data?.message || 'Failed to delete campaign' });
        }
    }
);

export const toggleGivingCampaign = createAsyncThunk(
    'givingCampaigns/toggleGivingCampaign',
    async (id, { rejectWithValue }) => {
        try {
            const token = localStorage.getItem('access_token');
            if (!token) throw new Error('No token available');

            const response = await apiClient.post(API_ENDPOINTS.GIVING_CAMPAIGN_TOGGLE(id));
            const { status, message, data } = response.data;

            if (status === 200) {
                return {
                    success: true,
                    message: message || 'Campaign status toggled',
                    id: id,
                    status: data?.status || 'Active'
                };
            }
            return rejectWithValue({ message: message || 'Failed to toggle campaign', status });
        } catch (error) {
            return rejectWithValue({ message: error.response?.data?.message || 'Failed to toggle campaign' });
        }
    }
);

export const duplicateGivingCampaign = createAsyncThunk(
    'givingCampaigns/duplicateGivingCampaign',
    async (id, { rejectWithValue }) => {
        try {
            const token = localStorage.getItem('access_token');
            if (!token) throw new Error('No token available');

            const response = await apiClient.post(API_ENDPOINTS.GIVING_CAMPAIGN_DUPLICATE(id));
            const { status, message, data } = response.data;

            if (status === 200) {
                return {
                    success: true,
                    message: message || 'Campaign duplicated successfully',
                    campaign: data || null
                };
            }
            return rejectWithValue({ message: message || 'Failed to duplicate campaign', status });
        } catch (error) {
            return rejectWithValue({ message: error.response?.data?.message || 'Failed to duplicate campaign' });
        }
    }
);

export const getGivingCampaignStats = createAsyncThunk(
    'givingCampaigns/getGivingCampaignStats',
    async (params = {}, { rejectWithValue }) => {
        try {
            const token = localStorage.getItem('access_token');
            if (!token) throw new Error('No token available');

            const response = await apiClient.get(API_ENDPOINTS.GIVING_CAMPAIGN_STATS, { params });
            const { status, message, data } = response.data;

            if (status === 200) {
                return {
                    success: true,
                    message: message || 'Statistics retrieved',
                    stats: data || {}
                };
            }
            return rejectWithValue({ message: message || 'Failed to fetch statistics', status });
        } catch (error) {
            return rejectWithValue({ message: error.response?.data?.message || 'Failed to fetch statistics' });
        }
    }
);

export const sendCampaignMessage = createAsyncThunk(
    'givingCampaigns/sendCampaignMessage',
    async ({ id, type, message }, { rejectWithValue }) => {
        try {
            const token = localStorage.getItem('access_token');
            if (!token) throw new Error('No token available');

            const response = await apiClient.post(API_ENDPOINTS.GIVING_CAMPAIGN_SEND_MESSAGE(id), {
                type,
                message
            });
            const { status, message: responseMessage, data } = response.data;

            if (status === 200) {
                return {
                    success: true,
                    message: responseMessage || 'Message sent successfully',
                    data: data || {}
                };
            }
            return rejectWithValue({ message: responseMessage || 'Failed to send message', status });
        } catch (error) {
            return rejectWithValue({ message: error.response?.data?.message || 'Failed to send message' });
        }
    }
);

export const getCampaignDonations = createAsyncThunk(
    'givingCampaigns/getCampaignDonations',
    async ({ id, params = {} }, { rejectWithValue }) => {
        try {
            const token = localStorage.getItem('access_token');
            if (!token) throw new Error('No token available');

            const response = await apiClient.get(API_ENDPOINTS.GIVING_CAMPAIGN_DONATIONS(id), { params });
            const { status, message, data } = response.data;

            if (status === 200) {
                return {
                    success: true,
                    message: message || 'Donations retrieved',
                    donations: data?.data || [],
                    pagination: data?.meta || {}
                };
            }
            return rejectWithValue({ message: message || 'Failed to fetch donations', status });
        } catch (error) {
            return rejectWithValue({ message: error.response?.data?.message || 'Failed to fetch donations' });
        }
    }
);

// =============================================
// ADD CAMPAIGN DONATION
// =============================================

export const addCampaignDonation = createAsyncThunk(
    'givingCampaigns/addCampaignDonation',
    async ({ id, data }, { rejectWithValue }) => {
        try {
            const token = localStorage.getItem('access_token');
            if (!token) throw new Error('No token available');

            const response = await apiClient.post(API_ENDPOINTS.GIVING_CAMPAIGN_ADD_DONATION(id), data);
            const { status, message, data: responseData } = response.data;

            if (status === 201 || status === 200) {
                return {
                    success: true,
                    message: message || 'Donation added successfully',
                    donation: responseData || null
                };
            }
            return rejectWithValue({ message: message || 'Failed to add donation', status });
        } catch (error) {
            return rejectWithValue({ message: error.response?.data?.message || 'Failed to add donation' });
        }
    }
);

// =============================================
// CONFIRM CAMPAIGN DONATION
// =============================================

export const confirmCampaignDonation = createAsyncThunk(
    'givingCampaigns/confirmCampaignDonation',
    async ({ campaignId, donationId }, { rejectWithValue }) => {
        try {
            const token = localStorage.getItem('access_token');
            if (!token) throw new Error('No token available');

            const response = await apiClient.post(API_ENDPOINTS.GIVING_CAMPAIGN_CONFIRM_DONATION(campaignId, donationId));
            const { status, message, data } = response.data;

            if (status === 200) {
                return {
                    success: true,
                    message: message || 'Donation confirmed successfully',
                    donation: data || null
                };
            }
            return rejectWithValue({ message: message || 'Failed to confirm donation', status });
        } catch (error) {
            return rejectWithValue({ message: error.response?.data?.message || 'Failed to confirm donation' });
        }
    }
);

// =============================================
// EXPORT CAMPAIGN
// =============================================

export const exportGivingCampaign = createAsyncThunk(
    'givingCampaigns/exportGivingCampaign',
    async (id, { rejectWithValue }) => {
        try {
            const token = localStorage.getItem('access_token');
            if (!token) throw new Error('No token available');

            const response = await apiClient.get(API_ENDPOINTS.GIVING_CAMPAIGN_EXPORT(id));
            const { status, message, data } = response.data;

            if (status === 200) {
                return {
                    success: true,
                    message: message || 'Campaign exported successfully',
                    data: data || {}
                };
            }
            return rejectWithValue({ message: message || 'Failed to export campaign', status });
        } catch (error) {
            return rejectWithValue({ message: error.response?.data?.message || 'Failed to export campaign' });
        }
    }
);

// =============================================
// SLICE
// =============================================

const initialState = {
    campaigns: [],
    pagination: {
        current_page: 1,
        last_page: 1,
        per_page: 15,
        total: 0
    },
    currentCampaign: null,
    donations: [],
    donationsPagination: {},
    stats: null,
    isLoading: false,
    isSubmitting: false,
    error: null,
    successMessage: null,
    apiMessage: null,
};

const givingCampaignSlice = createSlice({
    name: 'givingCampaigns',
    initialState,
    reducers: {
        clearError: (state) => { 
            state.error = null; 
        },
        clearSuccessMessage: (state) => { 
            state.successMessage = null; 
        },
        clearApiMessage: (state) => { 
            state.apiMessage = null; 
        },
        clearGivingCampaignState: (state) => {
            state.campaigns = [];
            state.pagination = { 
                current_page: 1, 
                last_page: 1, 
                per_page: 15, 
                total: 0 
            };
            state.currentCampaign = null;
            state.donations = [];
            state.donationsPagination = {};
            state.stats = null;
            state.error = null;
            state.successMessage = null;
            state.apiMessage = null;
            state.isLoading = false;
            state.isSubmitting = false;
        },
        clearCurrentCampaign: (state) => {
            state.currentCampaign = null;
            state.donations = [];
        },
    },
    extraReducers: (builder) => {
        builder
            // ===== GET CAMPAIGNS =====
            .addCase(getGivingCampaigns.pending, (state) => {
                state.isLoading = true;
                state.error = null;
                state.apiMessage = null;
            })
            .addCase(getGivingCampaigns.fulfilled, (state, action) => {
                state.isLoading = false;
                state.campaigns = action.payload.data?.data || [];
                state.pagination = action.payload.data?.meta || initialState.pagination;
                state.apiMessage = action.payload.message;
                state.successMessage = action.payload.message;
                state.error = null;
            })
            .addCase(getGivingCampaigns.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload?.message || 'Failed to fetch campaigns';
                state.apiMessage = action.payload?.message || 'Failed to fetch campaigns';
                state.campaigns = [];
            })

            // ===== GET SINGLE CAMPAIGN =====
            .addCase(getGivingCampaign.pending, (state) => {
                state.isLoading = true;
                state.error = null;
                state.apiMessage = null;
            })
            .addCase(getGivingCampaign.fulfilled, (state, action) => {
                state.isLoading = false;
                state.currentCampaign = action.payload.campaign || null;
                state.apiMessage = action.payload.message;
                state.successMessage = action.payload.message;
                state.error = null;
            })
            .addCase(getGivingCampaign.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload?.message || 'Failed to fetch campaign';
                state.apiMessage = action.payload?.message || 'Failed to fetch campaign';
                state.currentCampaign = null;
            })

            // ===== CREATE CAMPAIGN =====
            .addCase(createGivingCampaign.pending, (state) => {
                state.isSubmitting = true;
                state.error = null;
                state.apiMessage = null;
            })
            .addCase(createGivingCampaign.fulfilled, (state, action) => {
                state.isSubmitting = false;
                state.successMessage = action.payload.message;
                state.apiMessage = action.payload.message;
                state.error = null;
                if (action.payload.campaign) {
                    state.campaigns = [action.payload.campaign, ...state.campaigns];
                }
            })
            .addCase(createGivingCampaign.rejected, (state, action) => {
                state.isSubmitting = false;
                state.error = action.payload?.message || 'Failed to create campaign';
                state.apiMessage = action.payload?.message || 'Failed to create campaign';
            })

            // ===== UPDATE CAMPAIGN =====
            .addCase(updateGivingCampaign.pending, (state) => {
                state.isSubmitting = true;
                state.error = null;
                state.apiMessage = null;
            })
            .addCase(updateGivingCampaign.fulfilled, (state, action) => {
                state.isSubmitting = false;
                state.successMessage = action.payload.message;
                state.apiMessage = action.payload.message;
                state.error = null;
                if (action.payload.campaign) {
                    state.campaigns = state.campaigns.map(c =>
                        c.id === action.payload.campaign.id ? action.payload.campaign : c
                    );
                    if (state.currentCampaign?.id === action.payload.campaign.id) {
                        state.currentCampaign = action.payload.campaign;
                    }
                }
            })
            .addCase(updateGivingCampaign.rejected, (state, action) => {
                state.isSubmitting = false;
                state.error = action.payload?.message || 'Failed to update campaign';
                state.apiMessage = action.payload?.message || 'Failed to update campaign';
            })

            // ===== DELETE CAMPAIGN =====
            .addCase(deleteGivingCampaign.pending, (state) => {
                state.isSubmitting = true;
                state.error = null;
                state.apiMessage = null;
            })
            .addCase(deleteGivingCampaign.fulfilled, (state, action) => {
                state.isSubmitting = false;
                state.successMessage = action.payload.message;
                state.apiMessage = action.payload.message;
                state.error = null;
                if (action.payload.id) {
                    state.campaigns = state.campaigns.filter(c => c.id !== action.payload.id);
                    if (state.currentCampaign?.id === action.payload.id) {
                        state.currentCampaign = null;
                    }
                }
            })
            .addCase(deleteGivingCampaign.rejected, (state, action) => {
                state.isSubmitting = false;
                state.error = action.payload?.message || 'Failed to delete campaign';
                state.apiMessage = action.payload?.message || 'Failed to delete campaign';
            })

            // ===== TOGGLE CAMPAIGN =====
            .addCase(toggleGivingCampaign.pending, (state) => {
                state.isLoading = true;
                state.error = null;
                state.apiMessage = null;
            })
            .addCase(toggleGivingCampaign.fulfilled, (state, action) => {
                state.isLoading = false;
                state.successMessage = action.payload.message;
                state.apiMessage = action.payload.message;
                state.error = null;
                if (action.payload.id) {
                    state.campaigns = state.campaigns.map(c =>
                        c.id === action.payload.id ? { ...c, status: action.payload.status } : c
                    );
                }
            })
            .addCase(toggleGivingCampaign.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload?.message || 'Failed to toggle campaign';
                state.apiMessage = action.payload?.message || 'Failed to toggle campaign';
            })

            // ===== DUPLICATE CAMPAIGN =====
            .addCase(duplicateGivingCampaign.pending, (state) => {
                state.isLoading = true;
                state.error = null;
                state.apiMessage = null;
            })
            .addCase(duplicateGivingCampaign.fulfilled, (state, action) => {
                state.isLoading = false;
                state.successMessage = action.payload.message;
                state.apiMessage = action.payload.message;
                state.error = null;
                if (action.payload.campaign) {
                    state.campaigns = [action.payload.campaign, ...state.campaigns];
                }
            })
            .addCase(duplicateGivingCampaign.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload?.message || 'Failed to duplicate campaign';
                state.apiMessage = action.payload?.message || 'Failed to duplicate campaign';
            })

            // ===== GET STATS =====
            .addCase(getGivingCampaignStats.pending, (state) => {
                state.isLoading = true;
                state.error = null;
                state.apiMessage = null;
            })
            .addCase(getGivingCampaignStats.fulfilled, (state, action) => {
                state.isLoading = false;
                state.stats = action.payload.stats || null;
                state.apiMessage = action.payload.message;
                state.error = null;
            })
            .addCase(getGivingCampaignStats.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload?.message || 'Failed to fetch statistics';
                state.apiMessage = action.payload?.message || 'Failed to fetch statistics';
                state.stats = null;
            })

            // ===== GET DONATIONS =====
            .addCase(getCampaignDonations.pending, (state) => {
                state.isLoading = true;
                state.error = null;
                state.apiMessage = null;
            })
            .addCase(getCampaignDonations.fulfilled, (state, action) => {
                state.isLoading = false;
                state.donations = action.payload.donations || [];
                state.donationsPagination = action.payload.pagination || {};
                state.apiMessage = action.payload.message;
                state.error = null;
            })
            .addCase(getCampaignDonations.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload?.message || 'Failed to fetch donations';
                state.apiMessage = action.payload?.message || 'Failed to fetch donations';
                state.donations = [];
                state.donationsPagination = {};
            })

            // ===== ADD DONATION =====
            .addCase(addCampaignDonation.pending, (state) => {
                state.isSubmitting = true;
                state.error = null;
                state.apiMessage = null;
            })
            .addCase(addCampaignDonation.fulfilled, (state, action) => {
                state.isSubmitting = false;
                state.successMessage = action.payload.message;
                state.apiMessage = action.payload.message;
                state.error = null;
                // Optionally add donation to list if viewing current campaign
                if (action.payload.donation && state.currentCampaign) {
                    state.donations = [action.payload.donation, ...state.donations];
                }
            })
            .addCase(addCampaignDonation.rejected, (state, action) => {
                state.isSubmitting = false;
                state.error = action.payload?.message || 'Failed to add donation';
                state.apiMessage = action.payload?.message || 'Failed to add donation';
            })

            // ===== CONFIRM DONATION =====
            .addCase(confirmCampaignDonation.pending, (state) => {
                state.isSubmitting = true;
                state.error = null;
                state.apiMessage = null;
            })
            .addCase(confirmCampaignDonation.fulfilled, (state, action) => {
                state.isSubmitting = false;
                state.successMessage = action.payload.message;
                state.apiMessage = action.payload.message;
                state.error = null;
                // Update donation status in the list
                if (action.payload.donation) {
                    state.donations = state.donations.map(d =>
                        d.id === action.payload.donation.id ? action.payload.donation : d
                    );
                }
            })
            .addCase(confirmCampaignDonation.rejected, (state, action) => {
                state.isSubmitting = false;
                state.error = action.payload?.message || 'Failed to confirm donation';
                state.apiMessage = action.payload?.message || 'Failed to confirm donation';
            })

            // ===== SEND MESSAGE =====
            .addCase(sendCampaignMessage.pending, (state) => {
                state.isSubmitting = true;
                state.error = null;
                state.apiMessage = null;
            })
            .addCase(sendCampaignMessage.fulfilled, (state, action) => {
                state.isSubmitting = false;
                state.successMessage = action.payload.message;
                state.apiMessage = action.payload.message;
                state.error = null;
            })
            .addCase(sendCampaignMessage.rejected, (state, action) => {
                state.isSubmitting = false;
                state.error = action.payload?.message || 'Failed to send message';
                state.apiMessage = action.payload?.message || 'Failed to send message';
            })

            // ===== EXPORT CAMPAIGN =====
            .addCase(exportGivingCampaign.pending, (state) => {
                state.isLoading = true;
                state.error = null;
                state.apiMessage = null;
            })
            .addCase(exportGivingCampaign.fulfilled, (state, action) => {
                state.isLoading = false;
                state.successMessage = action.payload.message;
                state.apiMessage = action.payload.message;
                state.error = null;
            })
            .addCase(exportGivingCampaign.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload?.message || 'Failed to export campaign';
                state.apiMessage = action.payload?.message || 'Failed to export campaign';
            });
    },
});

export const {
    clearError,
    clearSuccessMessage,
    clearApiMessage,
    clearGivingCampaignState,
    clearCurrentCampaign,
} = givingCampaignSlice.actions;

// =============================================
// SELECTORS
// =============================================

export const selectGivingCampaigns = (state) => state.givingCampaigns?.campaigns || [];
export const selectGivingCampaignPagination = (state) => state.givingCampaigns?.pagination || { 
    current_page: 1, 
    last_page: 1, 
    per_page: 15, 
    total: 0 
};
export const selectCurrentGivingCampaign = (state) => state.givingCampaigns?.currentCampaign || null;
export const selectGivingCampaignStats = (state) => state.givingCampaigns?.stats || null;
export const selectGivingCampaignDonations = (state) => state.givingCampaigns?.donations || [];
export const selectGivingCampaignDonationsPagination = (state) => state.givingCampaigns?.donationsPagination || {};
export const selectGivingCampaignsLoading = (state) => state.givingCampaigns?.isLoading || false;
export const selectGivingCampaignsSubmitting = (state) => state.givingCampaigns?.isSubmitting || false;
export const selectGivingCampaignsError = (state) => state.givingCampaigns?.error || null;
export const selectGivingCampaignsApiMessage = (state) => state.givingCampaigns?.apiMessage || null;

export default givingCampaignSlice.reducer;